import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Link } from "react-router-dom";
import Navbar from "../../navbar";
import Header from "../../header";
import Pagination from "../dashboard/appPagination";
import CertificateUploadModel from "../applications/certificateUploadModel";
import {
  fetchApplications,
  updateApplication,
  addCertificateAndRenewal,
  clearApplications,
} from "../../../store/reducers/applicationSlice";
import { createNotification } from "../../../store/reducers/notificationSlice";
import { handleFilesDownload } from "../../../store/reducers/fileSlice";
import { fetchCertificates } from "../../../store/reducers/certificateSlice";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import iconSearch from "../../../assets/media/icon-search.svg";
import iconMenuDots from "../../../assets/media/icon-menu-dots.svg";
import { hasRole, ROLES } from "../../../utility/rbac";
import { getNotificationTemplate } from "../../../utility/notificationTemplates";

const columnHelper = createColumnHelper();

const ApprovedApp = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const authToken = user.access_token;

  const applications = useSelector((state) => state.applications);
  const applicationLoading = useSelector((state) => state.applications.fetchLoading);
  const getallUserdata = useSelector((state) => state.userdata.getalluserdata);
  const certificates = useSelector((state) => state.certificates);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchBy, setSearchBy] = useState("applicationId");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filteredApplication, setFilteredApplication] = useState([]);
  const [downloadingIds, setDownloadingIds] = useState([]);
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [seletctedApplicationId, setSelectedApplicationId] = useState(null);
  const [selectedCertificateFile, setSelectedCertificateFile] = useState(null);
  const [issuedTo, setIssuedTo] = useState(0);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [selectedRejectItem, setSelectedRejectItem] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);

  // Clear applications and fetch fresh data when component mounts or filters change
  useEffect(() => {
    // First clear the applications to ensure fresh data
    dispatch(clearApplications());

    // Then fetch applications with current filters
    const fetchData = async () => {
      // Ensure itemsPerPage is a valid positive number
      const validItemsPerPage = (typeof itemsPerPage === 'number' && itemsPerPage > 0) ? itemsPerPage : 10;

      // we'll fetch all applications and filter client-side
      let queryParams = {
        page: 1, // Always fetch all data from page 1
        limit: 1000, // Fetch a large number to ensure we get all records for client-side filtering
        state: "approved",
      };

      if (searchQuery && searchQuery.trim() !== '') {
        queryParams[searchBy] = searchQuery.trim();
      }

      if (startDate) {
        queryParams.createdFrom = moment(startDate).format("YYYY-MM-DD");
      }
      if (endDate) {
        queryParams.createdTo = moment(endDate).format("YYYY-MM-DD");
      }

      console.log('📡 Fetching applications for approved filtering with params:', queryParams);
      await dispatch(fetchApplications({ accessToken: authToken, queryParams }));
      await dispatch(fetchCertificates({ accessToken: authToken, queryParams }));
    };

    fetchData();
  }, [authToken, dispatch, startDate, endDate, currentPage, searchQuery, itemsPerPage, searchBy]);

  // Merge user data and filter applications
  useEffect(() => {
    const applicationsArray = Array.isArray(applications.data)
      ? applications.data
      : applications.data?.applications || applications.data?.data || [];

    if (applicationsArray.length > 0) {
      const mergedData = applicationsArray.map((appItem) => {
        const userItem = getallUserdata?.find?.(
          (userData) => userData.id === appItem.user_id
        );
        if (userItem) {
          return {
            ...appItem,
            applicationId: appItem.id,
            ...userItem,
            userId: userItem.id,
          };
        }
        return { ...appItem, applicationId: appItem.id };
      });

      const filteredData = mergedData.filter(
        (item) => {
          // Check both company_id and companyId fields since API data structure varies
          // const isCorrectCompany = (
          //   item.company_id === user.user.company_id ||
          //   item.companyId === user.user.company_id ||
          //   user.user.is_admin
          // );
          const isApproved = item.state === "approved";
          const isCorrectCompany = item.company_id === user.user.company_id || user.user.is_admin || ["reviewer", "viewer"].includes(user.user.user_role?.toLowerCase());

          const matchesSearch = searchQuery ? Object.values(item).some(
            (value) =>
              value &&
              value.toString().toLowerCase().includes(searchQuery.toLowerCase())
          ) : true; // If no search query, show all

          console.log('🔍 Filtering item:', {
            id: item.id,
            company_id: item.company_id,
            companyId: item.companyId,
            userCompanyId: user.user.company_id,
            isAdmin: user.user.is_admin,
            // isCorrectCompany,
            isApproved,
            matchesSearch,
            searchQuery
          });

          return isApproved && matchesSearch && isCorrectCompany;
        }
      );

      // Apply client-side pagination since server-side filtering by state doesn't work
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedData = filteredData.slice(startIndex, endIndex);

      console.log('📊 Filtered approved applications for display:', {
        totalFiltered: filteredData.length,
        paginated: paginatedData.length,
        currentPage,
        itemsPerPage,
        filtered: paginatedData.map(item => ({ id: item.applicationId, state: item.state, company_id: item.company_id }))
      });

      setFilteredApplication(paginatedData);
    } else {
      setFilteredApplication([]);
    }
  }, [searchQuery, applications.data, getallUserdata, user.user.company_id, user.user.is_admin, currentPage, itemsPerPage]);

  const handleSearchInputChange = (e) => {
    let value = e.target.value;
    if (searchBy === "applicationId") {
      value = value.replace(/^gb-?/i, "").replace(/\D/g, "");
    }
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleSearchByChange = (e) => {
    setSearchBy(e.target.value);
    setSearchQuery("");
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to page 1 when changing items per page
  };

  const getTotalPages = () => {
    // Since we're doing client-side filtering, we need to calculate based on filtered data
    const applicationsArray = Array.isArray(applications.data)
      ? applications.data
      : applications.data?.applications || applications.data?.data || [];

    if (applicationsArray.length > 0 && getallUserdata?.length > 0) {
      const mergedData = applicationsArray.map((appItem) => {
        const userItem = getallUserdata.find(
          (userData) => userData.id === appItem.user_id
        );
        if (userItem) {
          return {
            ...appItem,
            applicationId: appItem.id,
            ...userItem,
            userId: userItem.id,
          };
        }
        return { ...appItem, applicationId: appItem.id };
      });

      // Count only approved applications
      const approvedCount = mergedData.filter(
        (item) => {
          const isCorrectCompany = (
            item.company_id === user.user.company_id ||
            item.companyId === user.user.company_id ||
            user.user.is_admin
          );
          const isApproved = item.state === "approved";
          const matchesSearch = searchQuery ? Object.values(item).some(
            (value) =>
              value &&
              value.toString().toLowerCase().includes(searchQuery.toLowerCase())
          ) : true;
          return isCorrectCompany && isApproved && matchesSearch;
        }
      ).length;

      const validItemsPerPage = (typeof itemsPerPage === 'number' && itemsPerPage > 0) ? itemsPerPage : 10;
      const totalPages = Math.ceil(approvedCount / validItemsPerPage);
      // console.log('🚀 ~ getTotalPages ~ client-side approved count:', approvedCount, 'totalPages:', totalPages);
      return totalPages || 1;
    }

    // Fallback
    const validItemsPerPage = (typeof itemsPerPage === 'number' && itemsPerPage > 0) ? itemsPerPage : 10;
    const totalPages = Math.ceil(filteredApplication.length / validItemsPerPage);
    // console.log('🚀 ~ getTotalPages ~ fallback:', totalPages);
    return totalPages || 1;
  };

  const handleDownload = useCallback(async (item) => {
    const { applicationId, userId } = item;
    const id = applicationId || item.id;

    // Prevent multiple downloads - check if already downloading
    if (downloadingIds.includes(id)) {
      return;
    }

    setDownloadingIds((prev) => [...prev, id]);

    try {
      await handleFilesDownload({ userId, applicationId: id, authToken });
    } catch (error) {
      console.error("Download failed", error);
    } finally {
      setDownloadingIds((prev) => prev.filter((downloadId) => downloadId !== id));
    }
  }, [authToken, downloadingIds]);

  const handleChangeStatus = async (item, state, discrepancies = null) => {
    console.log('🔄 Changing status for application:', item.applicationId, 'to state:', state);

    await dispatch(
      updateApplication({
        id: item?.applicationId,
        state: state,
        descripencie: discrepancies, // Note: API expects 'descripencie' not 'discrepancies'
        accessToken: authToken,
      })
    ).then(() => {
      console.log('✅ Status updated, refetching approved applications...');

      // Refetch with proper query parameters to maintain approved filter
      const validItemsPerPage = (typeof itemsPerPage === 'number' && itemsPerPage > 0) ? itemsPerPage : 10;

      let queryParams = {
        page: 1, // Always fetch all data from page 1
        limit: 1000, // Fetch a large number to ensure we get all records for client-side filtering
        search: searchQuery,
        // Note: Server-side filtering by state doesn't work, so we do client-side filtering
      };

      if (startDate && endDate) {
        queryParams.start_date = startDate.toISOString();
        queryParams.end_date = endDate.toISOString();
      }

      console.log('🔄 Refetching with query params:', queryParams);
      dispatch(fetchApplications({ accessToken: authToken, queryParams }));

      const template = getNotificationTemplate(state, {
        ...item,
        userName: item.userName || item.user_name || 'User',
        discrepancies: discrepancies
      });

      dispatch(createNotification({
        message: template.message,
        userId: item.userId || item.user_id,
        subject: template.subject,
        htmlMailContent: template.html,
        sendToCompanyAdmins: true,
        accessToken: authToken,
        is_admin: false,
      }));
    });
  };

  const handleOpenReject = (item) => {
    console.log('🔴 Opening reject modal for item:', item);
    setSelectedRejectItem(item);
    setRejectReason("");
    setShowRejectModal(true);
  };

  const handleCloseReject = () => {
    setShowRejectModal(false);
    setSelectedRejectItem(null);
    setRejectReason("");
  };

  const handleConfirmReject = () => {
    console.log('🔴 Reject confirmation:', {
      selectedItem: selectedRejectItem,
      rejectReason: rejectReason,
      reasonLength: rejectReason?.length
    });

    if (!rejectReason || !rejectReason.trim()) {
      console.error('❌ No reject reason provided');
      return;
    }

    const updatedItem = { ...selectedRejectItem };
    // Handle both 'discrepancy' and 'discrepancies' field names
    const discrepancies =
      (selectedRejectItem.discrepancy && typeof selectedRejectItem.discrepancy === "object")
        ? { ...selectedRejectItem.discrepancy }
        : (selectedRejectItem.discrepancies && typeof selectedRejectItem.discrepancies === "object")
          ? { ...selectedRejectItem.discrepancies }
          : {};

    discrepancies.other = { value: rejectReason.trim(), check: true };
    updatedItem.discrepancies = discrepancies;

    console.log('🔴 Updated discrepancies:', discrepancies);

    setShowRejectModal(false);
    handleChangeStatus(updatedItem, 5, discrepancies); // State 5 = rejected
  };

  const handleShow = (item) => {
    setIssuedTo(item.userId);
    setSelectedApplicationId(item.applicationId);
    setSelectedApp(item);
    setShowCertificateModal(true);
  };

  const handleClose = () => {
    setShowCertificateModal(false);
    setSelectedCertificateFile(null);
    setSelectedApplicationId(null);
    setIssuedTo(0);
    setSelectedApp(null);
  };

  const handleCertificateUpload = (startDate, endDate) => {
    const payload = {
      issue_date: startDate,
      due_date: endDate,
      authToken,
      id: seletctedApplicationId,
      user_id: issuedTo,
    };

    if (selectedCertificateFile) {
      payload.file = new File(
        [selectedCertificateFile],
        `${Date.now()}-${selectedCertificateFile.name}`,
        { type: selectedCertificateFile.type }
      );
    }

    dispatch(addCertificateAndRenewal(payload)).then(() => {
      const template = getNotificationTemplate("certificate_created", {
        ...selectedApp,
        applicationId: seletctedApplicationId,
      });

      dispatch(
        createNotification({
          message: template.message,
          userId: issuedTo,
          subject: template.subject,
          htmlMailContent: template.html,
          sendToCompanyAdmins: true,
          accessToken: authToken,
          is_admin: false,
        })
      );
    });
    handleClose();
  };

  // Pagination and sorting data
  // Use server-side pagination - no need for client-side slicing
  const paginatedData = useMemo(() => {
    return filteredApplication
      .sort((a, b) => b.applicationId - a.applicationId);
  }, [filteredApplication]);

  // React Table columns
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "serial",
        header: "S.NO.",
        cell: (info) => (currentPage - 1) * itemsPerPage + info.row.index + 1,
      }),
      columnHelper.accessor("applicationId", {
        header: "Application No.",
        cell: (info) => `GB-${info.getValue()}`,
      }),
      columnHelper.display({
        id: "applicationName",
        header: "Application Name",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardTitle">
              <strong title={row.name}>
                {row.name?.length > 50
                  ? `${row.name.substring(0, 50)}...`
                  : row.name}
              </strong>
              <span>{row.categoryName}</span>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "submittedBy",
        header: "Submitted By",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardTitle">
              <strong>{row.companyName || row.company_name || 'N/A'}</strong>
              <span>{row.companyEmail || row.company_email || 'N/A'}</span>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "submittedFor",
        header: "Submitted For",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardTitle">
              <strong>{row.userName || row.user_name || 'N/A'}</strong>
              <span>{row.userEmail || row.user_email || row.email || 'N/A'}</span>
            </div>
          );
        },
      }),
      columnHelper.accessor("updated_at", {
        header: "Submitted Date",
        cell: (info) => <span className="dateText">{info.getValue()?.slice(0, 10) || "-"}</span>,
      }),
      columnHelper.accessor("issue_date", {
        header: "Registered Date",
        cell: (info) => <span className="dateText">{info.getValue()?.slice(0, 10) || "-"}</span>,
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardActions">
              <div className="dropdown d-inline-flex">
                <button
                  className="application-dropdown dropdown-toggle border-0 bg-transparent"
                  type="button"
                  id="dropdownMenuButton"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <img src={iconMenuDots} alt="Menu Dots" />
                </button>
                <ul
                  className="dropdown-menu"
                  aria-labelledby="dropdownMenuButton"
                >
                  <li>
                    {downloadingIds.includes(row.applicationId || row.id) ? (
                      <span className="dropdown-item disabled" style={{ cursor: 'not-allowed', opacity: 0.6, pointerEvents: 'none' }}>
                        Downloading...
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="dropdown-item"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const id = row.applicationId || row.id;
                          if (!downloadingIds.includes(id)) {
                            handleDownload(row);
                          }
                        }}
                        style={{ cursor: 'pointer', border: 'none', background: 'transparent', width: '100%', textAlign: 'left', padding: '0 16px' }}
                      >
                        Download
                      </button>
                    )}
                  </li>
                  {!hasRole(user, ROLES.VIEWER) && (
                    <>
                      <li>
                        <Link
                          onClick={() => handleChangeStatus(row, 1)}
                          className="dropdown-item"
                        >
                          Move to In process
                        </Link>
                      </li>
                      <li>
                        <Link
                          onClick={() => handleOpenReject(row)}
                          className="dropdown-item"
                        >
                          Move to Rejected
                        </Link>
                      </li>
                      <li>
                        <Link
                          onClick={() => handleShow(row, 7)}
                          className="dropdown-item"
                        >
                          Certificate & Renewal
                        </Link>
                      </li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          );
        },
      }),
    ],
    [downloadingIds, certificates.data, navigate, getallUserdata, handleDownload, currentPage, itemsPerPage]
  );

  const table = useReactTable({
    data: paginatedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="mainApp">
      <Navbar />
      <Header />
      <div className="wrapper">
        <div className="boxAppBoard boxWhite">
          {/* Header with search + date filter */}
          <div className="boardHeader">
            <div className="boardHeadLeft">
              <h2>Approved Applications</h2>
            </div>
            <div className="boardHeadRight">
              <div className="">
                <select onChange={handleSearchByChange} value={searchBy}>
                  <option value="applicationId">Search by Application ID</option>
                  <option value="companyName">Search by Company Name</option>
                  <option value="username">Search by Employee Name</option>
                  {/* <option value="email">Search by Email </option> */}
                </select>
              </div>
              <div className="searchForm">
                <input
                  type="text"
                  placeholder={searchBy === "applicationId" ? "GB-XXX" : "Search"}
                  onChange={handleSearchInputChange}
                  value={searchBy === "applicationId" && searchQuery ? `GB-${searchQuery}` : searchQuery}
                  className="searchInput"
                />
                <button type="button" className="searchBtn">
                  <img src={iconSearch} alt="search" />
                </button>
              </div>
              <div className="datePicker">
                <DatePicker
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(update) => {
                    const [start, end] = update;
                    setStartDate(start);
                    setEndDate(end);
                    setCurrentPage(1);
                  }}
                  isClearable={true}
                  placeholderText="Select Date Range"
                  className="date-range-input"
                  dateFormat="yyyy-MM-dd"
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="boardContent table-responsive" style={{ position: 'relative' }}>
            {/* Loader overlay for table section */}
            {applicationLoading && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 10,
                  borderRadius: '4px'
                }}
              >
                <div className="text-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2 mb-0">Loading applications...</p>
                </div>
              </div>
            )}
            <table className="table">
              <thead className="">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr className="" key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id}>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody>
                {applicationLoading ? (
                  <tr>
                    <td colSpan={table.getHeaderGroups()?.[0]?.headers.length} style={{ textAlign: "center", padding: "20px" }}>
                      <div className="spinner-border spinner-border-sm text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : table.getRowModel().rows.length > 0 ? (
                  table.getRowModel().rows.map((row) => (
                    <tr className="" key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={table.getHeaderGroups()?.[0]?.headers.length} style={{ textAlign: "center", padding: "20px" }}>
                      {applicationLoading ? "Loading..." : "Result not found"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <CertificateUploadModel
            handleFileUpload={handleCertificateUpload}
            handleClose={handleClose}
            showModal={showCertificateModal}
            selectedCertificateFile={selectedCertificateFile}
            setSelectedCertificateFile={setSelectedCertificateFile}
          />

          {/* Reject Modal */}
          <Modal show={showRejectModal} onHide={handleCloseReject} centered>
            <Modal.Header closeButton>
              <Modal.Title>Reject Application</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="mb-3">
                <label htmlFor="rejectReason" className="form-label">
                  Reason for Rejection <span className="text-danger">*</span>
                </label>
                <textarea
                  id="rejectReason"
                  className="form-control"
                  rows="4"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Please provide a reason for rejecting this application..."
                  required
                />
                {!rejectReason.trim() && (
                  <div className="text-danger small mt-1">
                    Please provide a reason for rejection
                  </div>
                )}
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseReject}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleConfirmReject}
                disabled={!rejectReason.trim()}
              >
                Reject Application
              </Button>
            </Modal.Footer>
          </Modal>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={getTotalPages}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            totalItems={(() => {
              // Calculate total approved applications for client-side filtering
              const applicationsArray = Array.isArray(applications.data)
                ? applications.data
                : applications.data?.applications || applications.data?.data || [];

              if (applicationsArray.length > 0 && getallUserdata?.length > 0) {
                const mergedData = applicationsArray.map((appItem) => {
                  const userItem = getallUserdata.find(
                    (userData) => userData.id === appItem.user_id
                  );
                  if (userItem) {
                    return {
                      ...appItem,
                      applicationId: appItem.id,
                      ...userItem,
                      userId: userItem.id,
                    };
                  }
                  return { ...appItem, applicationId: appItem.id };
                });

                return mergedData.filter(
                  (item) => {
                    const isCorrectCompany = (
                      item.company_id === user.user.company_id ||
                      item.companyId === user.user.company_id ||
                      user.user.is_admin
                    );
                    const isApproved = item.state === "approved";
                    const matchesSearch = searchQuery ? Object.values(item).some(
                      (value) =>
                        value &&
                        value.toString().toLowerCase().includes(searchQuery.toLowerCase())
                    ) : true;
                    return isCorrectCompany && isApproved && matchesSearch;
                  }
                ).length;
              }

              return filteredApplication.length;
            })()}
          />
        </div>
      </div>
    </div>
  );
};

export default ApprovedApp;